import type {
  BottomButton,
  ColorScheme,
  MainButtonParams,
  SafeAreaInset,
  SecondaryButtonParams,
  WebApp,
} from "./types";

/**
 * The single seam between the app and Telegram.
 *
 * Two rules from "01 Foundations" are enforced here rather than by convention:
 *
 *  1. Only `colorScheme` crosses this boundary as a *styling* signal. themeParams
 *     is never read for surfaces — a user on a green custom theme recolours
 *     Telegram's chrome, and our canvas resolves to light or dark and inherits
 *     nothing else. The header seam and the bottom buttons are the exceptions,
 *     and they are pushed *out* to Telegram (setHeaderColor / setParams) rather
 *     than pulled in.
 *
 *  2. Nothing may depend on a fixed viewport height. Height is published as CSS
 *     custom properties and re-published on every viewportChanged event.
 */

/** Canvas colours, kept in sync with tokens.css by hand — Telegram needs literals. */
const CANVAS: Record<ColorScheme, string> = {
  light: "#FAFCFE",
  dark: "#0D1826",
};

/** Mint action colours for the native buttons, per the dark-canvas shift rule. */
const ACTION: Record<ColorScheme, { bg: string; fg: string }> = {
  light: { bg: "#14996A", fg: "#FFFFFF" },
  dark: { bg: "#3FCD96", fg: "#06281C" },
};

function detect(): WebApp | null {
  const app = typeof window !== "undefined" ? window.Telegram?.WebApp : undefined;
  // Outside Telegram the script still defines the object but leaves initData
  // empty; treat that as "not hosted" so dev in a plain browser degrades rather
  // than throwing on the first native call.
  return app ?? null;
}

const webApp = detect();

/**
 * True when running inside a real Telegram client. The script defines the
 * WebApp object in any browser but only a real client signs initData — an
 * empty string means "script loaded, nobody home".
 */
export const isHosted = Boolean(webApp && webApp.initData);

/** Feature-detect by Bot API version before touching an optional member. */
export function supports(version: string): boolean {
  if (!webApp?.isVersionAtLeast) return false;
  try {
    return webApp.isVersionAtLeast(version);
  } catch {
    return false;
  }
}

export function raw(): WebApp | null {
  return webApp;
}

/* ------------------------------------------------------------------ */
/* Viewport                                                            */
/* ------------------------------------------------------------------ */

function publishViewport(): void {
  const root = document.documentElement;
  const height = webApp?.viewportHeight ?? window.innerHeight;
  const stable = webApp?.viewportStableHeight ?? window.innerHeight;

  root.style.setProperty("--tg-viewport-height", `${height}px`);
  root.style.setProperty("--tg-viewport-stable-height", `${stable}px`);
  // The gap between the two is the keyboard (or an in-progress expand). Screens
  // that must not sit under the keyboard read this instead of guessing.
  root.style.setProperty("--tg-keyboard-inset", `${Math.max(0, stable - height)}px`);
}

function publishSafeArea(): void {
  const root = document.documentElement;
  const zero: SafeAreaInset = { top: 0, bottom: 0, left: 0, right: 0 };
  // safeAreaInset is 8.0+; content insets land under Telegram's own chrome.
  const safe = (supports("8.0") && webApp?.safeAreaInset) || zero;
  const content = (supports("8.0") && webApp?.contentSafeAreaInset) || zero;

  root.style.setProperty("--tg-safe-top", `${safe.top + content.top}px`);
  root.style.setProperty("--tg-safe-bottom", `${safe.bottom + content.bottom}px`);
  root.style.setProperty("--tg-safe-left", `${safe.left + content.left}px`);
  root.style.setProperty("--tg-safe-right", `${safe.right + content.right}px`);
}

/* ------------------------------------------------------------------ */
/* Colour scheme                                                       */
/* ------------------------------------------------------------------ */

export function colorScheme(): ColorScheme {
  // Inside a real client its scheme is authoritative; outside, the stub
  // object reports "light" regardless, so dev fallbacks take over.
  if (isHosted && webApp?.colorScheme) return webApp.colorScheme;
  const override = new URLSearchParams(window.location.search).get("scheme");
  if (override === "dark" || override === "light") return override;
  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyScheme(): void {
  const scheme = colorScheme();
  document.documentElement.setAttribute("data-scheme", scheme);

  // Push our canvas colour outward so the header seam disappears instead of
  // banding against whatever the user's theme painted there.
  try {
    webApp?.setHeaderColor(CANVAS[scheme]);
    webApp?.setBackgroundColor(CANVAS[scheme]);
    if (supports("7.10")) webApp?.setBottomBarColor?.(CANVAS[scheme]);
  } catch {
    // Older clients reject hex for setHeaderColor; the seam is cosmetic.
  }
}

const schemeListeners = new Set<(scheme: ColorScheme) => void>();

export function onSchemeChange(cb: (scheme: ColorScheme) => void): () => void {
  schemeListeners.add(cb);
  return () => schemeListeners.delete(cb);
}

/* ------------------------------------------------------------------ */
/* Init                                                                */
/* ------------------------------------------------------------------ */

let initialised = false;

export function init(): void {
  if (initialised) return;
  initialised = true;

  publishViewport();
  publishSafeArea();
  applyScheme();

  if (!webApp) {
    window.addEventListener("resize", publishViewport);
    return;
  }

  webApp.ready();
  webApp.expand();

  // Swipe-down-to-close otherwise fires on any vertical drag inside the canvas —
  // scrollable feeds and the chat transcript both trigger it.
  if (supports("7.7")) {
    try {
      webApp.disableVerticalSwipes?.();
    } catch {
      /* older client: overscroll-behavior in base.css is the fallback */
    }
  }

  webApp.onEvent("viewportChanged", publishViewport);
  webApp.onEvent("themeChanged", () => {
    applyScheme();
    const scheme = colorScheme();
    for (const cb of schemeListeners) cb(scheme);
  });

  if (supports("8.0")) {
    webApp.onEvent("safeAreaChanged", publishSafeArea);
    webApp.onEvent("contentSafeAreaChanged", publishSafeArea);
  }
}

/* ------------------------------------------------------------------ */
/* Native bottom buttons                                               */
/* ------------------------------------------------------------------ */

/**
 * MainButton is Telegram-rendered and lives *below* our viewport — we never
 * draw our own bottom-fixed primary. Returns a cleanup that unbinds the click
 * handler and hides the button, so screens can own it without leaking state.
 */
export function setMainButton(params: MainButtonParams & { onClick?: () => void }): () => void {
  if (!webApp) return () => {};

  const { onClick, ...rest } = params;
  const scheme = colorScheme();
  const button = webApp.MainButton;

  button.setParams({
    color: ACTION[scheme].bg,
    text_color: ACTION[scheme].fg,
    is_active: true,
    is_visible: true,
    ...rest,
  });

  if (onClick) button.onClick(onClick);

  return () => {
    if (onClick) button.offClick(onClick);
    button.hide();
  };
}

export function setSecondaryButton(
  params: SecondaryButtonParams & { onClick?: () => void },
): () => void {
  // SecondaryButton is 7.10+; on older clients the screen simply has one CTA.
  if (!webApp || !supports("7.10") || !webApp.SecondaryButton) return () => {};

  const { onClick, ...rest } = params;
  const button: BottomButton = webApp.SecondaryButton;

  button.setParams({ is_active: true, is_visible: true, position: "left", ...rest });
  if (onClick) button.onClick(onClick);

  return () => {
    if (onClick) button.offClick(onClick);
    button.hide();
  };
}

export function hideMainButton(): void {
  webApp?.MainButton.hide();
}

/**
 * BackButton lives in Telegram's header — screens must not draw their own
 * top-left chevron.
 */
export function setBackButton(onClick: () => void): () => void {
  if (!webApp) return () => {};

  const button = webApp.BackButton;
  button.onClick(onClick);
  button.show();

  return () => {
    button.offClick(onClick);
    button.hide();
  };
}

export function hideBackButton(): void {
  webApp?.BackButton.hide();
}

/* ------------------------------------------------------------------ */
/* Haptics                                                             */
/* ------------------------------------------------------------------ */

export const haptic = {
  tap(): void {
    webApp?.HapticFeedback?.impactOccurred("light");
  },
  press(): void {
    webApp?.HapticFeedback?.impactOccurred("medium");
  },
  select(): void {
    webApp?.HapticFeedback?.selectionChanged();
  },
  success(): void {
    webApp?.HapticFeedback?.notificationOccurred("success");
  },
  warning(): void {
    webApp?.HapticFeedback?.notificationOccurred("warning");
  },
  error(): void {
    webApp?.HapticFeedback?.notificationOccurred("error");
  },
};

/* ------------------------------------------------------------------ */
/* Handoff — the only place platform blue is allowed to mean something */
/* ------------------------------------------------------------------ */

/** Opens a Telegram surface (chat, group, share). Always leaves the canvas. */
export function openTelegram(url: string): void {
  haptic.tap();
  if (webApp) {
    webApp.openTelegramLink(url);
  } else {
    window.open(url, "_blank", "noopener");
  }
}

export function openExternal(url: string): void {
  if (webApp) {
    webApp.openLink(url);
  } else {
    window.open(url, "_blank", "noopener");
  }
}

/** Prompts Telegram's add-to-home-screen flow (Bot API 8.0+). */
export function addToHomeScreen(): boolean {
  const app = webApp as (WebApp & { addToHomeScreen?: () => void }) | null;
  if (!supports("8.0") || !app?.addToHomeScreen) return false;
  app.addToHomeScreen();
  return true;
}

export function shareToStory(mediaUrl: string, text?: string): boolean {
  if (!supports("7.8") || !webApp?.shareToStory) return false;
  webApp.shareToStory(mediaUrl, text ? { text } : undefined);
  return true;
}

/** Deep-link payload from `t.me/<bot>/<app>?startapp=<payload>`. */
export function startParam(): string | undefined {
  return webApp?.initDataUnsafe?.start_param;
}

export function telegramUser() {
  return webApp?.initDataUnsafe?.user;
}

/** Signed payload. Only meaningful server-side, where the HMAC can be checked. */
export function initData(): string {
  return webApp?.initData ?? "";
}

/* ------------------------------------------------------------------ */
/* CloudStorage — per-user, survives reinstalls, ~1024 keys             */
/* ------------------------------------------------------------------ */

export const cloud = {
  get(key: string): Promise<string | null> {
    const storage = webApp?.CloudStorage;
    if (!supports("6.9") || !storage) {
      return Promise.resolve(localStorage.getItem(key));
    }
    return new Promise((resolve) => {
      storage.getItem(key, (err, value) => resolve(err ? null : (value ?? null)));
    });
  },

  set(key: string, value: string): Promise<boolean> {
    const storage = webApp?.CloudStorage;
    if (!supports("6.9") || !storage) {
      localStorage.setItem(key, value);
      return Promise.resolve(true);
    }
    return new Promise((resolve) => {
      storage.setItem(key, value, (err) => resolve(!err));
    });
  },

  remove(key: string): Promise<boolean> {
    const storage = webApp?.CloudStorage;
    if (!supports("6.9") || !storage) {
      localStorage.removeItem(key);
      return Promise.resolve(true);
    }
    return new Promise((resolve) => {
      storage.removeItem(key, (err) => resolve(!err));
    });
  },
};

export function showAlert(message: string): void {
  if (webApp) webApp.showAlert(message);
  else window.alert(message);
}

export function showConfirm(message: string): Promise<boolean> {
  if (!webApp) return Promise.resolve(window.confirm(message));
  return new Promise((resolve) => webApp.showConfirm(message, resolve));
}
